import React, { useCallback, useEffect, useState } from "react";
import {
  bulkInviteAdminService,
  AdminBulkInviteCampaign,
  BulkInviteCampaignStatus,
  BULK_INVITE_PRIORITY,
} from "../../services/admin/bulkInvite.admin.service";
import { DataTable, DataTablePagination } from "@/components/ui/data-table";
import type { ColumnDef } from "@tanstack/react-table";
import { Mail, RefreshCw, Search, XCircle } from "lucide-react";
import { toast } from "react-hot-toast";
import { format } from "date-fns";

const STATUS_OPTIONS: { label: string; value: string }[] = [
  { label: "All statuses", value: "ALL" },
  { label: "Queued", value: "QUEUED" },
  { label: "Sending", value: "SENDING" },
  { label: "Waiting", value: "WAITING" },
  { label: "Halted", value: "HALTED" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Failed", value: "FAILED" },
  { label: "Cancelled", value: "CANCELLED" },
];

const PRIORITY_OPTIONS = [
  { label: "High", value: BULK_INVITE_PRIORITY.HIGH },
  { label: "Normal", value: BULK_INVITE_PRIORITY.NORMAL },
  { label: "Low", value: BULK_INVITE_PRIORITY.LOW },
];

function priorityLabel(priority: number): string {
  const match = PRIORITY_OPTIONS.find((o) => o.value === priority);
  return match ? match.label : `Custom (${priority})`;
}

function statusBadgeClasses(status: BulkInviteCampaignStatus): string {
  switch (status) {
    case "COMPLETED":
      return "bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300";
    case "SENDING":
    case "WAITING":
    case "QUEUED":
      return "bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300";
    case "HALTED":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-300";
    case "CANCELLED":
      return "bg-muted text-muted-foreground";
    case "FAILED":
      return "bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300";
    default:
      return "bg-muted text-muted-foreground";
  }
}

const TERMINAL_STATUSES: BulkInviteCampaignStatus[] = [
  "HALTED",
  "COMPLETED",
  "FAILED",
  "CANCELLED",
];

export default function BulkInvites() {
  const [campaigns, setCampaigns] = useState<AdminBulkInviteCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [status, setStatus] = useState("ALL");
  const [search, setSearch] = useState("");
  const [cancelTarget, setCancelTarget] = useState<AdminBulkInviteCampaign | null>(
    null
  );
  const [cancelling, setCancelling] = useState(false);
  const [priorityUpdatingId, setPriorityUpdatingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await bulkInviteAdminService.listCampaigns({
        page,
        limit: pageSize,
        status,
        search: search.trim() || undefined,
      });
      setCampaigns(res.data);
      setTotal(res.pagination.total);
      setTotalPages(res.pagination.totalPages || 1);
    } catch (error) {
      console.error("Failed to load bulk invite jobs:", error);
      toast.error("Failed to load bulk invite jobs");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, status, search]);

  React.useEffect(() => {
    void load();
  }, [load]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handlePriorityChange = async (
    campaign: AdminBulkInviteCampaign,
    priority: number
  ) => {
    setPriorityUpdatingId(campaign.id);
    const previous = campaign.priority;
    setCampaigns((prev) =>
      prev.map((c) => (c.id === campaign.id ? { ...c, priority } : c))
    );
    try {
      await bulkInviteAdminService.setCampaignPriority(campaign.id, priority);
      toast.success(`Priority set to ${priorityLabel(priority)}`);
    } catch (error) {
      setCampaigns((prev) =>
        prev.map((c) => (c.id === campaign.id ? { ...c, priority: previous } : c))
      );
      toast.error(
        error instanceof Error ? error.message : "Failed to update priority"
      );
    } finally {
      setPriorityUpdatingId(null);
    }
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await bulkInviteAdminService.cancelCampaign(cancelTarget.id);
      toast.success(`Job cancelled for ${cancelTarget.brandName}`);
      setCancelTarget(null);
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to cancel job");
    } finally {
      setCancelling(false);
    }
  };

  const columns: ColumnDef<AdminBulkInviteCampaign>[] = [
    {
      id: "brand",
      header: "Brand",
      meta: { width: "w-44" },
      cell: ({ row }) => (
        <div className="font-medium text-foreground truncate">
          {row.original.brandName}
        </div>
      ),
    },
    {
      id: "email",
      header: "Email",
      meta: { width: "w-52" },
      cell: ({ row }) =>
        row.original.brandEmail ? (
          <div className="flex items-center gap-1 text-xs text-muted-foreground min-w-0">
            <Mail size={12} className="shrink-0" />
            <span className="truncate">{row.original.brandEmail}</span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      id: "status",
      header: "Status",
      meta: { width: "w-32" },
      cell: ({ row: { original: c } }) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadgeClasses(
            c.status
          )}`}
          title={c.haltReason || undefined}
        >
          {c.status}
        </span>
      ),
    },
    {
      id: "progress",
      header: "Progress",
      meta: { width: "w-48" },
      cell: ({ row: { original: c } }) => {
        const done = c.sentCount + c.failedCount + c.bouncedCount + c.cancelledCount;
        const pct = c.totalRecipients > 0 ? Math.min(100, Math.round((done / c.totalRecipients) * 100)) : 0;
        return (
          <div className="w-40">
            <div className="flex justify-between text-xs text-muted-foreground mb-1">
              <span>
                {c.sentCount}/{c.totalRecipients} sent
              </span>
              <span>{pct}%</span>
            </div>
            <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-[#d7785b] rounded-full"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      },
    },
    {
      id: "bounced",
      header: "Bounced / Failed",
      meta: { width: "w-32" },
      cell: ({ row: { original: c } }) => (
        <span className="text-sm text-muted-foreground">
          {c.bouncedCount} / {c.failedCount}
        </span>
      ),
    },
    {
      id: "priority",
      header: "Priority",
      meta: { width: "w-32" },
      cell: ({ row: { original: c } }) => (
        <select
          value={c.priority}
          disabled={priorityUpdatingId === c.id || TERMINAL_STATUSES.includes(c.status)}
          onChange={(e) => handlePriorityChange(c, Number(e.target.value))}
          className="rounded-lg border border-border px-2 py-1.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {PRIORITY_OPTIONS.some((o) => o.value === c.priority) ? null : (
            <option value={c.priority}>{priorityLabel(c.priority)}</option>
          )}
          {PRIORITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ),
    },
    {
      id: "created",
      header: "Created",
      meta: { width: "w-40" },
      cell: ({ row: { original: c } }) => (
        <span className="text-sm text-muted-foreground">
          {format(new Date(c.createdAt), "MMM dd, yyyy HH:mm")}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      meta: { width: "w-28" },
      cell: ({ row: { original: c } }) => (
        <button
          type="button"
          onClick={() => setCancelTarget(c)}
          disabled={TERMINAL_STATUSES.includes(c.status)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/30 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
        >
          <XCircle size={14} />
          Cancel
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Bulk Invite Jobs
          </h1>
          <p className="text-sm text-muted-foreground">
            Monitor, prioritize, and cancel brands&apos; bulk affiliate invite sends.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-border rounded-lg hover:bg-accent"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-muted-foreground" />
          </div>
          <input
            type="text"
            placeholder="Search by brand name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#d7785b]/20"
          />
        </div>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="rounded-lg border border-border px-3 py-2.5 text-sm"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-card rounded-xl border border-border shadow-sm">
        <DataTable
          data={campaigns}
          columns={columns}
          isLoading={loading}
          emptyText="No bulk invite jobs found"
        />

        <DataTablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          pageSizeOptions={[10, 20, 50, 100]}
          onPageChange={setPage}
          noun="jobs"
        />
      </div>

      {cancelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md bg-card rounded-xl border border-border shadow-xl p-5 space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                Cancel bulk invite job?
              </h2>
              <p className="text-sm text-muted-foreground mt-2">
                This will stop the send for{" "}
                <span className="font-medium text-foreground">
                  {cancelTarget.brandName}
                </span>
                . All pending invitations for this job will be permanently deleted.
                Emails already sent, bounced, or failed will keep their recorded stats.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setCancelTarget(null)}
                disabled={cancelling}
                className="px-3 py-2 text-sm rounded-lg border border-border hover:bg-accent"
              >
                Keep job
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={cancelling}
                className="px-3 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {cancelling ? "Cancelling…" : "Cancel job"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
